import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tag, TagInsert, TagUpdate, ContactTag, ContactWithTags, TagWithCount } from "@/types/tags";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { toast } from "@/hooks/use-toast";

export function useTags() {
  const [loading, setLoading] = useState(false);
  const { currentOrg } = useOrganizationContext();
  const queryClient = useQueryClient();

  // Buscar todas as tags da organização
  const { data: tags, isLoading: tagsLoading, refetch: refetchTags } = useQuery({
    queryKey: ["tags", currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg?.id) return [];

      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .eq("org_id", currentOrg.id)
        .order("name", { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!currentOrg?.id,
    staleTime: 5 * 60_000,
  });

  // Buscar tags com contagem de contatos
  const { data: tagsWithCount, isLoading: tagsWithCountLoading } = useQuery({
    queryKey: ["tags-with-count", currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg?.id) return [];

      const { data, error } = await supabase
        .from("tags")
        .select(`
          *,
          contact_tags(count)
        `)
        .eq("org_id", currentOrg.id)
        .order("name", { ascending: true });

      if (error) throw error;

      return (data || []).map(tag => ({
        ...tag,
        contact_count: tag.contact_tags?.[0]?.count || 0
      })) as TagWithCount[];
    },
    enabled: !!currentOrg?.id,
    staleTime: 5 * 60_000,
  });

  // Criar nova tag
  const createTag = useMutation({
    mutationFn: async (tagData: Omit<TagInsert, 'org_id'>) => {
      if (!currentOrg?.id) throw new Error("Organização não selecionada");

      const { data, error } = await supabase
        .from("tags")
        .insert({
          ...tagData,
          org_id: currentOrg.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tags-with-count"] });
      toast({
        title: "Tag criada",
        description: "A tag foi criada com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao criar tag: " + error.message,
        variant: "destructive",
      });
    },
  });

  // Atualizar tag
  const updateTag = useMutation({
    mutationFn: async ({ id, ...tagData }: TagUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from("tags")
        .update(tagData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tags-with-count"] });
      toast({
        title: "Tag atualizada",
        description: "A tag foi atualizada com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao atualizar tag: " + error.message,
        variant: "destructive",
      });
    },
  });

  // Deletar tag
  const deleteTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase
        .from("tags")
        .delete()
        .eq("id", tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tags-with-count"] });
      toast({
        title: "Tag deletada",
        description: "A tag foi deletada com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao deletar tag: " + error.message,
        variant: "destructive",
      });
    },
  });

  // Atribuir tag a contato
  const assignTagToContact = useMutation({
    mutationFn: async ({ contactId, tagId }: { contactId: string; tagId: string }) => {
      const { data, error } = await supabase
        .from("contact_tags")
        .insert({
          contact_id: contactId,
          tag_id: tagId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["tags-with-count"] });
    },
  });

  // Remover tag de contato
  const removeTagFromContact = useMutation({
    mutationFn: async ({ contactId, tagId }: { contactId: string; tagId: string }) => {
      const { error } = await supabase
        .from("contact_tags")
        .delete()
        .eq("contact_id", contactId)
        .eq("tag_id", tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["tags-with-count"] });
    },
  });

  // Buscar contatos com suas tags
  const getContactsWithTags = async (): Promise<ContactWithTags[]> => {
    if (!currentOrg?.id) return [];

    const { data, error } = await supabase
      .from("contacts")
      .select(`
        *,
        contact_tags(
          tags(*)
        )
      `)
      .eq("org_id", currentOrg.id)
      .order("name", { ascending: true });

    if (error) throw error;

    return (data || []).map(contact => ({
      ...contact,
      tags: contact.contact_tags?.map((ct: any) => ct.tags) || []
    }));
  };

  return {
    tags,
    tagsWithCount,
    tagsLoading,
    tagsWithCountLoading,
    createTag,
    updateTag,
    deleteTag,
    assignTagToContact,
    removeTagFromContact,
    getContactsWithTags,
    refetchTags,
  };
}
